import { useCallback, useRef, useState } from "react";
import { WalletSetupStep } from "@/constants/enums";
import { useRegisterWalletOwnerKey } from "@/hooks/mutations/useRegisterWalletOwnerKey";
import { useCurrentOrganization } from "@/hooks/queries";
import { ApiError } from "@/lib/apiClient";
import { deriveOwnerPublicKey } from "@/lib/wallet/ownerKey";
import {
  generateSecretPhrase,
  type SecretPhrase,
} from "@/lib/wallet/secretPhrase";
import {
  isWalletSetupLockedStep,
  setWalletSetupNavigationLocked,
} from "@/lib/wallet/walletSetupLock";
import { OrganizationType } from "@/types/domain";

type EntityType = "merchant" | "financier";

/** A generated key whose registration the server has not confirmed. */
type PendingRegistration = {
  words: SecretPhrase;
  ownerPublicKey: string;
  entityType: EntityType;
  entityId: string | undefined;
  outcomeUnknown: boolean;
};

function entityTypeFromOrganization(
  externalEntityType: string | undefined,
): EntityType | undefined {
  if (externalEntityType === OrganizationType.Merchant) return "merchant";
  if (externalEntityType === OrganizationType.Financier) return "financier";
  return undefined;
}

/** True only when the error proves this key was not attached. */
function isDefinitiveRejection(error: unknown, outcomeUnknown: boolean): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === "WALLET_ALREADY_ATTACHED") return true;
  if (outcomeUnknown) return false;
  const { statusCode } = error;
  return statusCode >= 400 && statusCode < 500 && statusCode !== 408 && statusCode !== 429;
}

/**
 * Welcome → loading → save | error.
 * Phrase is generated in memory, used to derive a P-256 public key, then shown
 * only after the register call succeeds. Never written to storage.
 *
 * If the outcome is unknown (e.g. lost response), the phrase stays in memory
 * and retry re-sends the same owner key
 */
export function useWalletSetupFlow() {
  const { data: organization } = useCurrentOrganization();
  const { mutateAsync } = useRegisterWalletOwnerKey();
  const inFlightRef = useRef(false);
  const pendingRef = useRef<PendingRegistration | null>(null);
  const [step, setStep] = useState<WalletSetupStep>(WalletSetupStep.Welcome);
  const [phrase, setPhrase] = useState<SecretPhrase | null>(null);
  const [hasPending, setHasPending] = useState(false);

  const setPending = useCallback((next: PendingRegistration | null) => {
    pendingRef.current = next;
    setHasPending(next !== null);
  }, []);

  const goTo = useCallback((next: WalletSetupStep) => {
    setStep(next);
    setWalletSetupNavigationLocked(
      isWalletSetupLockedStep(next) || pendingRef.current !== null,
    );
  }, []);

  const createWallet = useCallback(async () => {
    const entityType = entityTypeFromOrganization(
      organization?.externalEntityType,
    );
    if (!entityType || inFlightRef.current) return;
    const entityId = organization?.externalEntityId ?? undefined;
    inFlightRef.current = true;
    goTo(WalletSetupStep.Loading);
    try {
      let pending = pendingRef.current;
      if (
        pending
        && (pending.entityType !== entityType || pending.entityId !== entityId)
      ) {
        setPending(null);
        pending = null;
      }
      if (!pending) {
        const words = generateSecretPhrase();
        const ownerPublicKey = await deriveOwnerPublicKey(words);
        pending = { words, ownerPublicKey, entityType, entityId, outcomeUnknown: false };
        setPending(pending);
      }
      await mutateAsync({
        ownerPublicKey: pending.ownerPublicKey,
        entityId,
        entityType,
      });
      setPhrase(pending.words);
      setPending(null);
      goTo(WalletSetupStep.Save);
    } catch (error) {
      const pending = pendingRef.current;
      if (pending) {
        setPending(
          isDefinitiveRejection(error, pending.outcomeUnknown)
            ? null
            : { ...pending, outcomeUnknown: true },
        );
      }
      setPhrase(null);
      goTo(WalletSetupStep.Error);
    } finally {
      inFlightRef.current = false;
    }
  }, [goTo, mutateAsync, organization?.externalEntityId, organization?.externalEntityType, setPending]);

  const retry = useCallback(() => {
    if (pendingRef.current) {
      void createWallet();
      return;
    }
    setPhrase(null);
    goTo(WalletSetupStep.Welcome);
  }, [createWallet, goTo]);

  const complete = useCallback(() => {
    setPhrase(null);
    setPending(null);
    goTo(WalletSetupStep.Welcome);
  }, [goTo, setPending]);

  const locked = isWalletSetupLockedStep(step) || hasPending;

  return { step, phrase, locked, createWallet, retry, complete };
}
